import { Alert, FormControl, FormControlLabel, FormHelperText, Stack, Switch } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { usePermissionSets } from '../usePermissionSets'
import { isWorkflowClosedToEveryone } from './isWorkflowClosedToEveryone'
import { useUpdateWorkflowRestriction, useWorkflowRestriction } from './useWorkflowRestriction'
import type { RecordControlProps } from './recordControlProps'

/**
 * Saves on every flip, apart from the dialog's per-tab Save. A workflow's grants count only while it is
 * restricted, so they are shown only then.
 */
export const WorkflowRestrictionToggle = ({ recordId, locked, grants, savedGrants }: RecordControlProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'record-access' })
  const { data, isError: loadFailed } = useWorkflowRestriction(recordId)
  const restrictionUpdate = useUpdateWorkflowRestriction(recordId)
  const { data: setsData } = usePermissionSets()

  // An unread switch would show the workflow as open, which it may not be.
  if (loadFailed && data == null) return <Alert severity="error">{t('restriction.load-error')}</Alert>

  const isRestricted = restrictionUpdate.isPending ? restrictionUpdate.variables : (data?.isRestricted ?? false)
  // Not the optimistic value: a grant staged while a switch-on is in flight would vanish if it is
  // rejected, and Save would still send it.
  const grantsApply = !restrictionUpdate.isPending && data?.isRestricted === true
  // Until the catalogue loads every custom set reads as conferring nothing, which would flash the warning.
  const closedToEveryone =
    setsData != null &&
    isWorkflowClosedToEveryone({
      isRestricted: grantsApply,
      grants: savedGrants,
      setPermissionsById: new Map(setsData.permissionSets.map((set) => [set.id, set.permissions])),
    })

  return (
    <>
      <Stack gap={1}>
        <FormControl fullWidth disabled={data == null || restrictionUpdate.isPending || locked}>
          <FormControlLabel
            label={t('restriction.label')}
            labelPlacement="start"
            sx={{ width: '100%', justifyContent: 'space-between' }}
            control={<Switch checked={isRestricted} onChange={(event) => restrictionUpdate.mutate(event.target.checked)} />}
          />
          <FormHelperText sx={{ mt: 1 }}>{t('restriction.helper')}</FormHelperText>
        </FormControl>
        {restrictionUpdate.isError && <Alert severity="error">{t('restriction.save-error')}</Alert>}
        {closedToEveryone && <Alert severity="warning">{t('restriction.closed-to-everyone')}</Alert>}
      </Stack>
      {grantsApply && grants}
    </>
  )
}
